/**
 * Vendor suspension status hook for apps/sell.
 *
 * Reads from `useVendorContext` (which already hydrates the active
 * vendor row including `status`, `suspended_at`, and `suspended_reason`)
 * and derives a small, focused shape every editable page consumes:
 *
 *   const { isSuspended, reason, suspendedAt } = useVendorSuspensionStatus();
 *
 * Pages then either:
 *
 *   1. Wrap their form region in `<SuspendedGate suspended={isSuspended} ... />`
 *      from `@pc/shop-ui` to grey-out + lock the inputs.
 *   2. Per-row CTAs (products list, order detail action buttons) read
 *      `isSuspended` directly to set `disabled` + `title` tooltips.
 *
 * The SellShell separately renders the persistent
 * `<VendorSuspendedBanner reason={reason} />` at the top of every
 * page so the operator sees the lockdown context immediately.
 *
 * NOTE: this hook is deliberately a thin derivation -- the underlying
 * VendorContext already paginated + RLS-gated the vendor row, so we
 * don't need a separate RPC round-trip here. The companion
 * `shop.rpc_get_my_vendor_suspension_state()` SDF (shipped in the same
 * migration) is exposed so other surfaces (Astro pages, edge functions)
 * can read suspension state without needing the full vendor row.
 */

import { useMemo } from 'react';
import { useVendorContext } from '../auth/VendorContext';

export interface VendorSuspensionStatus {
  /** True when the active vendor's `status = 'suspended'`. */
  isSuspended: boolean;
  /** Captured suspension reason or null when the vendor isn't suspended. */
  reason: string | null;
  /** ISO timestamp when the vendor was suspended; null otherwise. */
  suspendedAt: string | null;
}

export function useVendorSuspensionStatus(): VendorSuspensionStatus {
  const { vendor } = useVendorContext();
  return useMemo<VendorSuspensionStatus>(() => {
    if (!vendor) {
      return { isSuspended: false, reason: null, suspendedAt: null };
    }
    if (vendor.status !== 'suspended') {
      return { isSuspended: false, reason: null, suspendedAt: null };
    }
    return {
      isSuspended: true,
      reason: vendor.suspended_reason,
      suspendedAt: vendor.suspended_at,
    };
  }, [vendor]);
}
